import { toast } from 'react-toastify';
import { RootState, store } from '@redux/store';
import { UserRoles } from '@models/user.model';
import { TrialExpiredToast } from '@components/molecules/TrialExpiredToast';

/**
 * Error code the backend's SubscriptionWriteInterceptor puts in `extensions.code`.
 * Keep in sync with TRIAL_EXPIRED_ERROR_CODE in
 * zh-backend/src/common/interceptors/subscription-write.interceptor.ts.
 */
export const TRIAL_EXPIRED_ERROR_CODE = 'SUBSCRIPTION_TRIAL_EXPIRED';

/** react-toastify dedupes by id, so a page firing several blocked mutations shows one toast. */
export const TRIAL_EXPIRED_TOAST_ID = 'trial-expired';

/**
 * Single-toast mechanism for the trial paywall (sprint-15, story-072).
 *
 * `responseMiddleware` always shows this module's toast first, then rethrows (by design —
 * several hooks depend on the rethrow for non-toast handling). The calling hook's own
 * `onError` then fires its own `toast.error(...)`, producing a second, redundant toast for the
 * same failure. Editing every one of the ~198 `toast.error` call sites to special-case this one
 * error code is out of scope for this sprint, so this module patches `toast.error` itself,
 * once, at import time: react-toastify's own `toast()` implementation is a silent no-op
 * whenever a toast with the same `toastId` is already active, so forcing the *next*
 * `toast.error` call to reuse `TRIAL_EXPIRED_TOAST_ID` lets react-toastify's own dedup collapse
 * it into nothing extra on screen — no new import, no per-hook edit, anywhere else.
 *
 * `pendingSuppression` is consumed synchronously by the very next `toast.error` call (the
 * overwhelmingly common case, since the failed mutation's own `onError` runs as a microtask
 * before any macrotask). The `setTimeout(0)` below is only a safety-net auto-clear for hooks
 * whose `onError` never calls `toast.error` at all (e.g. ones that just set local state) — it
 * bounds the flag's exposure to "later in this same tick" rather than leaving it able to
 * swallow an unrelated, later failure.
 */
let pendingSuppression = false;

const realToastError = toast.error;
toast.error = ((content: Parameters<typeof toast.error>[0], options?: Parameters<typeof toast.error>[1]) => {
  if (pendingSuppression) {
    pendingSuppression = false;
    return realToastError(content, { ...options, toastId: TRIAL_EXPIRED_TOAST_ID });
  }
  return realToastError(content, options);
}) as typeof toast.error;

/**
 * True when any error in the response is the paywall refusing a write.
 *
 * Also accepts the nested `extensions.code.code` shape: the backend used to throw a Nest
 * HttpException whose body was the code object, and the mobile client still defends against
 * that form. Costs one comparison and keeps this honest if the error path ever changes.
 */
export const hasTrialExpiredError = (errors: any[] | undefined): boolean =>
  !!errors?.some((error: any) => {
    const code = error?.extensions?.code;
    return (
      code === TRIAL_EXPIRED_ERROR_CODE ||
      code?.code === TRIAL_EXPIRED_ERROR_CODE
    );
  });

/**
 * Explains a refused write to the user.
 *
 * Raised from the transport layer rather than from individual hooks: the block applies to
 * every mutation in the app, and several call paths swallow their own errors entirely.
 */
export const notifyTrialExpired = (): void => {
  const state: RootState = store.getState();
  const isOwner = state.user?.role === UserRoles.OWNER;

  toast.warning(
    <TrialExpiredToast
      isOwner={isOwner}
      onChoosePlan={() => {
        // Full navigation rather than history.push: this module lives outside the Router.
        window.location.href = '/trial-expired';
      }}
    />,
    {
      toastId: TRIAL_EXPIRED_TOAST_ID,
      // The container sets closeOnClick globally, which would eat the button press.
      closeOnClick: false,
      // Longer than the 5s default because there is something to act on.
      autoClose: 10000,
      // TrialExpiredToast draws its own icon beside the title; toastify's would be a second,
      // vertically-centered duplicate.
      icon: false,
    },
  );

  // Arm the dedup for the calling hook's own toast.error, which fires right after this
  // function returns (responseMiddleware rethrows, and the failed mutation's onError runs
  // as a microtask). See the module-level comment above for the full ordering analysis.
  pendingSuppression = true;
  setTimeout(() => {
    pendingSuppression = false;
  }, 0);
};
